import { useToast } from '@app/core/notifications';
import { SocialPlatformId } from '@app/modules/social-export/domain/platforms';
import { fragmentsDeliveryAPIClientEnableDelivery } from '@mi-front/api-clients';
import {
  useFragmentsCreateFragmentSocialPublish,
  useWebAppCreateFragmentCreation,
  WebAppCreateFragmentDto,
} from '@mi-front/api-v2';
import { PartialRecord } from '@mi-front/core';
import { useIntl } from 'react-intl';
import { FragmentCreationResponseDto } from '../fragment.types';
import { getQuickPublishedPlatforms } from '../lib/get-quick-published-platforms';
import { routeGenerator } from '../routing/FragmentsRoutes';
import { CreateDefaultFragmentData, CreateJointFragmentData } from '../types';

type CreateFragmentData = (CreateDefaultFragmentData | CreateJointFragmentData) & {
  platformUpload?: PartialRecord<
    SocialPlatformId,
    { enabled: boolean; connectionId?: string; config?: unknown }
  >;
  doExternalExport?: boolean;
};

interface FragmentCreateOptions {
  /** Called after the fragment is created on the BFF (response is `{ id }`). */
  onSuccess?: (created: FragmentCreationResponseDto) => void | Promise<void>;
  onError?: (error: unknown) => void;
}

export function useFragmentCreate(options?: FragmentCreateOptions) {
  const { formatMessage } = useIntl();
  const { notifySuccess, notifyError } = useToast();
  const createFragmentMutation = useWebAppCreateFragmentCreation({
    mutation: {},
  });
  const socialPublishStart = useFragmentsCreateFragmentSocialPublish({
    mutation: {},
  });

  async function save(data: CreateFragmentData) {
    try {
      const { platformUpload, doExternalExport, ...rest } = data;
      const quickPublishedPlatforms = getQuickPublishedPlatforms(platformUpload);

      // WebApp create DTO schema lags FragmentsCreateFragmentDto; BFF accepts quickPublishedPlatforms.
      const fragment = await createFragmentMutation.mutateAsync({
        data: {
          ...rest,
          quickPublishedPlatforms,
        } as unknown as WebAppCreateFragmentDto,
      });

      if (doExternalExport) {
        try {
          await fragmentsDeliveryAPIClientEnableDelivery(fragment.id, {});
        } catch {
          notifyError(
            formatMessage({
              id: 'fragments.addScreen.externalExportError',
              defaultMessage: 'No se pudo iniciar el export externo',
            }),
          );
        }
      }

      if (platformUpload) {
        for (const p in platformUpload) {
          const platform = p as SocialPlatformId;
          const uploadConfig = platformUpload[platform];

          if (uploadConfig?.enabled) {
            if (!uploadConfig.connectionId) {
              notifyError(
                formatMessage({
                  id: 'fragments.addScreen.exportMissingConnection',
                  defaultMessage: 'No se pudo iniciar el export: falta la cuenta de publicación',
                }),
              );
              continue;
            }

            await socialPublishStart
              .mutateAsync({
                fragmentId: fragment.id,
                data: {
                  connectionId: uploadConfig.connectionId,
                  data: (uploadConfig.config ?? {}) as never,
                },
              })
              .catch((err: unknown) => {
                console.error(err);
                notifyError(
                  formatMessage({
                    id: 'fragments.addScreen.exportError',
                    defaultMessage: 'No se pudo iniciar el export externo',
                  }),
                );
              });
          }
        }
      }

      if (options?.onSuccess) {
        await options.onSuccess(fragment);
      } else {
        notifySuccess(
          formatMessage({
            id: 'fragments.addScreen.created',
            defaultMessage: 'Fragmento creado',
          }),
          {
            link: {
              text: formatMessage({
                id: 'fragments.addScreen.goTo',
                defaultMessage: 'Ir al fragmento',
              }),
              to: routeGenerator.view(fragment.id),
            },
          },
        );
      }
    } catch (err: unknown) {
      if (options?.onError) {
        options.onError(err);
      } else {
        notifyError(
          formatMessage({
            id: 'fragments.addScreen.createError',
            defaultMessage: 'No se pudo crear el fragmento',
          }),
        );
      }

      throw err;
    }
  }

  return save;
}
